import { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useJournal, useDeleteJournalEntry } from '../hooks/useJournal'
import { Skeleton } from '../components/ui'

export default function Journal() {
  const { data: entries, isLoading } = useJournal()
  const deleteEntry = useDeleteJournalEntry()
  const [search, setSearch] = useState('')
  const [tagFilter, setTagFilter] = useState('')

  const allTags = useMemo(() => {
    const set = new Set<string>()
    for (const e of entries ?? []) {
      for (const t of e.tags ?? []) set.add(t)
    }
    return Array.from(set).sort()
  }, [entries])

  const filtered = useMemo(() => {
    const all = entries ?? []
    const q = search.toLowerCase()
    return all.filter((e) => {
      const matchesSearch = !q || e.content.toLowerCase().includes(q) ||
        (e.title ?? '').toLowerCase().includes(q) ||
        (e.tags ?? []).some((t) => t.toLowerCase().includes(q))
      const matchesTag = !tagFilter || (e.tags ?? []).includes(tagFilter)
      return matchesSearch && matchesTag
    })
  }, [entries, search, tagFilter])

  if (isLoading) return (
    <div style={{ padding: 'var(--space-6)', maxWidth: 720 }}>
      {Array.from({ length: 5 }, (_, i) => (
        <Skeleton key={i} height={60} style={{ marginBottom: 8 }} />
      ))}
    </div>
  )

  return (
    <div style={{ padding: 'var(--space-6)', maxWidth: 720 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-4)' }}>
        <h1>Journal</h1>
        <Link
          to="/journal/new"
          style={{
            padding: 'var(--space-2) var(--space-4)',
            background: 'var(--color-primary)',
            color: 'white',
            borderRadius: 'var(--radius-md)',
            fontWeight: 600,
            fontSize: 'var(--font-size-sm)',
            textDecoration: 'none',
          }}
        >
          + New entry
        </Link>
      </div>

      <input
        type="search"
        placeholder="Search entries…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={{
          width: '100%',
          padding: 'var(--space-3)',
          borderRadius: 'var(--radius-md)',
          border: '1px solid var(--color-border)',
          background: 'var(--color-bg)',
          color: 'var(--color-text)',
          marginBottom: 'var(--space-3)',
          boxSizing: 'border-box',
        }}
      />

      {allTags.length > 0 && (
        <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap', marginBottom: 'var(--space-4)' }}>
          <button
            onClick={() => setTagFilter('')}
            style={{
              padding: '4px 10px',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-xs)',
              fontWeight: 600,
              background: !tagFilter ? 'var(--color-primary)' : 'var(--color-surface)',
              color: !tagFilter ? 'white' : 'var(--color-text-muted)',
              border: '1px solid var(--color-border)',
            }}
          >
            All
          </button>
          {allTags.map((tag) => (
            <button
              key={tag}
              onClick={() => setTagFilter(tagFilter === tag ? '' : tag)}
              style={{
                padding: '4px 10px',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-xs)',
                fontWeight: 600,
                background: tagFilter === tag ? 'var(--color-primary)' : 'var(--color-surface)',
                color: tagFilter === tag ? 'white' : 'var(--color-text-muted)',
                border: '1px solid var(--color-border)',
              }}
            >
              {tag}
            </button>
          ))}
        </div>
      )}

      {(entries ?? []).length === 0 ? (
        <div style={{ padding: 'var(--space-8)', textAlign: 'center', background: 'var(--color-surface)', borderRadius: 'var(--radius-lg)', color: 'var(--color-text-muted)' }}>
          No journal entries yet — tap &quot;New entry&quot; to start.
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ padding: 'var(--space-6)', textAlign: 'center', background: 'var(--color-surface)', borderRadius: 'var(--radius-lg)', color: 'var(--color-text-muted)' }}>
          No entries match your search.
        </div>
      ) : (
        <div
          style={{
            maxHeight: '70vh',
            overflowY: 'auto',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-lg)',
            padding: 'var(--space-3)',
            background: 'var(--color-bg)',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {filtered.map((entry) => (
              <li
                key={entry.id}
                style={{
                  background: 'var(--color-surface)',
                  borderRadius: 'var(--radius-lg)',
                  padding: 'var(--space-4)',
                  marginBottom: 'var(--space-3)',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--space-3)', marginBottom: 'var(--space-1)' }}>
                      <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-faint)' }}>
                        {entry.entry_date}
                      </span>
                      {entry.title && (
                        <span style={{ fontWeight: 600 }}>{entry.title}</span>
                      )}
                    </div>
                    <p style={{
                      fontSize: 'var(--font-size-sm)',
                      color: 'var(--color-text-muted)',
                      margin: 0,
                      marginBottom: 'var(--space-2)',
                      whiteSpace: 'pre-wrap',
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}>
                      {entry.content}
                    </p>
                    {(entry.tags ?? []).length > 0 && (
                      <div style={{ display: 'flex', gap: 'var(--space-1)', flexWrap: 'wrap' }}>
                        {entry.tags.map((tag) => (
                          <span
                            key={tag}
                            style={{
                              fontSize: 'var(--font-size-xs)',
                              padding: '2px 8px',
                              borderRadius: 'var(--radius-sm)',
                              background: 'var(--color-surface-2)',
                              color: 'var(--color-text-muted)',
                            }}
                          >
                            #{tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 'var(--space-2)', marginLeft: 'var(--space-3)', flexShrink: 0 }}>
                    <Link
                      to={`/journal/${entry.id}/edit`}
                      style={{ color: 'var(--color-primary)', fontSize: 'var(--font-size-xs)', fontWeight: 600 }}
                    >
                      Edit
                    </Link>
                    <button
                      onClick={() => {
                        if (window.confirm('Delete this entry?')) deleteEntry.mutate(entry.id)
                      }}
                      style={{ color: 'var(--color-danger)', fontSize: 'var(--font-size-xs)' }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
